import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { AddIcon } from 'assets/icons';
import { deleteSellerRequested, selectSellersByCountry } from 'domains/centralSettings/CentralSettings.store';
import { SectionWrapper } from 'pages/SettingsPage/General/GeneralSettings.styled';
import { BlackButton, Flex, Icon, Text } from 'UI';
import SellerForm from './SellerForm';

interface SellerManagementProps {
  countryCode: string;
}

const SellerManagement = ({ countryCode }: SellerManagementProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const sellers = useSelector((state: RootState) => selectSellersByCountry(state, countryCode));
  const [hasDraft, setHasDraft] = useState(false);
  const [editingSellerName, setEditingSellerName] = useState<string | undefined>();

  useEffect(() => {
    setHasDraft(false);
    setEditingSellerName(undefined);
  }, [countryCode]);

  return (
    <SectionWrapper data-cy={'seller-management'} $marginY={15}>
      <Flex direction={'column'} gap={16} padding={'16px 0'}>
        <Flex gap={10} justify="space-between" wrap="wrap">
          <Flex direction="column" gap={4}>
            <Text type="heading4" disableGutter>
              {t('backoffice.central-settings.sellers.title', 'Seller Management')}
            </Text>
            <Text type="lg">
              {t('backoffice.central-settings.sellers.description', 'Add new Seller and select your Data providers.')}
            </Text>
          </Flex>
          <BlackButton
            onClick={() => setHasDraft(true)}
            data-cy="seller-management-add"
            disabled={hasDraft || editingSellerName !== undefined}
          >
            {t('backoffice.central-settings.sellers.add-button', 'Add new seller')}
            <Icon IconComponent={AddIcon} size={14} color="white" noPointer />
          </BlackButton>
        </Flex>
        {sellers.length === 0 && !hasDraft && (
          <Text type="base" data-cy="seller-management-empty" color={'error'}>
            {t('backoffice.central-settings.sellers.empty', 'No sellers configured for this country.')}
          </Text>
        )}
        {(sellers.length > 0 || hasDraft) && (
          <Flex direction="row" wrap="wrap" gap={24}>
            {sellers.map((seller) => (
              <SellerForm
                key={seller.name}
                countryCode={countryCode}
                seller={seller}
                editing={editingSellerName === seller.name}
                editDisabled={hasDraft || (editingSellerName !== undefined && editingSellerName !== seller.name)}
                onEdit={() => setEditingSellerName(seller.name)}
                onDiscard={() => {
                  setEditingSellerName((current) => (current === seller.name ? undefined : current));
                  dispatch(deleteSellerRequested({ countryCode, sellerName: seller.name }));
                }}
                onSaved={() => setEditingSellerName(undefined)}
              />
            ))}
            {hasDraft && (
              <SellerForm
                key="draft"
                countryCode={countryCode}
                editing
                onDiscard={() => setHasDraft(false)}
                onSaved={() => setHasDraft(false)}
              />
            )}
          </Flex>
        )}
      </Flex>
    </SectionWrapper>
  );
};

export default SellerManagement;
